import { XMarkIcon } from "@heroicons/react/20/solid";
import type { AuditScoreType, ContainerLevelHistory, ContainerStatusEntity, Dispatch } from "@ots-frontend/ots-data";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { OtsBadge } from "../components/OtsBadge";
import { OtsActionButton, OtsIconActionButton } from "../components/OtsButton";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import { OtsSegmentedControl } from "../components/OtsSegmentedControl";
import { OtsTableLoader } from "../components/OtsTableLoader";
import type { OtsTimeSeriesMarker } from "../components/OtsTimeSeriesChart";
import { formatActivityDateTime, formatActivityQuantity, formatActivityTime } from "../driver-activity/driver-activity-format";
import type { ActivityEntry } from "../driver-activity/model/activity-types";
import {
  type ContainerDetailsRange,
  containerTicketId,
  type ContainerTicketParty,
  detectHaulDrops,
  matchHaulDrops,
  pickupsFrom,
  summarizeContainerDetails,
} from "./container-details-model";
import { ContainerDetailsChart } from "./ContainerDetailsChart";
import { ContainerStatusIndicators } from "./ContainerStatusIndicators";
import { ContainerTicketList } from "./ContainerTicketList";

/** One part of the panel's data: what loaded, whether it is loading, and why it failed. */
export type ContainerDetailsSource<T> = {
  data: T;
  isLoading?: boolean;
  /** A message for the reader; the section offers Try again. */
  error?: string;
  onRetry?: () => void;
};

export type ContainerDetailsPanelProps = {
  containerId: string;
  containerName: string;
  siteName?: string;
  /** The grid's row for the tank: its indicators, tags, fill, and capacity. */
  status?: ContainerStatusEntity;
  /** Shows the stale-poll clock among the indicators. */
  stalePoll?: boolean;
  range: ContainerDetailsRange;
  onRangeChange: (range: ContainerDetailsRange) => void;
  onClose: () => void;
  /** The tank's tickets, as Driver Activity rows, newest first. */
  tickets: ContainerDetailsSource<readonly ActivityEntry[]>;
  levels: ContainerDetailsSource<readonly ContainerLevelHistory[]>;
  /** The tank's latest dispatch; shown when it is still open. */
  dispatch?: Dispatch;
  /** The start of today, for today's hauls. */
  dayStart: number;
  timeZone?: string;
  auditScoreType?: AuditScoreType;
  /** Whether ticket rows name the hauler (HubGo) or the driver (DispatchGo). */
  ticketParty?: ContainerTicketParty;
  onOpenTicket?: (entry: ActivityEntry) => void;
  onOpenAudit?: (entry: ActivityEntry) => void;
  onOpenAttachments?: (entry: ActivityEntry) => void;
  className?: string;
};

const ranges: { id: ContainerDetailsRange; label: string }[] = [
  { id: "24h", label: "24 h" },
  { id: "3d", label: "3 d" },
  { id: "7d", label: "7 d" },
];

const heading = "text-[10px] font-bold uppercase tracking-wide text-[var(--ots-text-muted)]";

const Section = ({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) => (
  <section aria-label={title} className="border-t border-[var(--ots-outline-variant)] px-3 py-3">
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className={heading}>{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

const Tile = ({ label, value, detail, tone }: { label: string; value: ReactNode; detail?: ReactNode; tone?: "primary" | "danger" }) => (
  <div className={[
    "min-w-0 rounded-md border px-2.5 py-2",
    tone === "primary"
      ? "border-[var(--ots-outline)] bg-[var(--ots-primary-container)]"
      : tone === "danger"
        ? "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)]"
        : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)]",
  ].join(" ")}>
    <div className="text-[11px] text-[var(--ots-text-muted)]">{label}</div>
    <div className="truncate text-sm font-semibold tabular-nums text-[var(--ots-text)]">{value}</div>
    {detail && <div className="truncate text-[11px] text-[var(--ots-text-muted)]">{detail}</div>}
  </div>
);

const formatNumber = (value: number | undefined, digits = 0) =>
  value == null ? "—" : value.toLocaleString(undefined, { maximumFractionDigits: digits });

/** The tank as the grid row has it: how full, how fast it fills, and what can be hauled now. */
const TankFacts = ({ status, timeZone }: { status: ContainerStatusEntity; timeZone?: string }) => {
  const fill = status.meterFill;
  const percent = fill?.actualPercent;
  const critical = fill?.actualStatus === "CRITICAL";
  const uom = status.waterMeterLevel?.volumeUom ?? status.oilMeterLevel?.volumeUom ?? "bbl";
  const metered = [status.waterMeterLevel?.volume, status.oilMeterLevel?.volume].filter((value): value is number => value != null);
  // The fill's own volume, else the meters' latest, else what the percent says of the capacity.
  const volume = fill?.actualVolume
    ?? (metered.length ? metered.reduce((sum, value) => sum + value, 0) : undefined)
    ?? (percent != null && status.containerVolume ? percent * status.containerVolume : undefined);
  return (
    <div className="space-y-2">
      {percent != null && (
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="font-semibold tabular-nums">
              {formatNumber(volume)}{status.containerVolume ? ` / ${formatNumber(status.containerVolume)}` : ""} {uom}
            </span>
            <span className={["tabular-nums", critical ? "font-semibold text-[var(--ots-error)]" : "text-[var(--ots-text-muted)]"].join(" ")}>
              {formatNumber(percent * 100)}% full
            </span>
          </div>
          <div role="meter" aria-label="How full" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent * 100)}
            className="h-2 overflow-hidden rounded-full bg-[var(--ots-surface-variant)]">
            <div className={["h-full rounded-full", critical ? "bg-[var(--ots-error)]" : "bg-[var(--ots-primary)]"].join(" ")}
              style={{ width: `${Math.min(Math.max(percent, 0), 1) * 100}%` }}/>
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Tile label="Fill rate" value={fill?.rate != null ? `${formatNumber(fill.rate, 1)}/h` : "—"}/>
        <Tile label="Time to full" tone={fill?.actualTimeToFullCritical ? "danger" : undefined}
          value={fill?.actualTimeToFull != null ? `${formatNumber(fill.actualTimeToFull, 1)} h` : "—"}/>
        <Tile label="Avail. loads" value={formatNumber(fill?.availableLoadCount)}
          detail={fill?.availableLoadVolume ? formatActivityQuantity(fill.availableLoadVolume, uom) : undefined}/>
      </div>
      <p className="text-[11px] text-[var(--ots-text-muted)]">Polled {fill?.dttm ? formatActivityDateTime(fill.dttm, timeZone) : "never"}</p>
    </div>
  );
};

const Tags = ({ status }: { status: ContainerStatusEntity }) => !status.containerTags?.length ? null : (
  <ul aria-label="Tags" className="flex flex-wrap gap-1">
    {status.containerTags.map((tag) => (
      <li key={tag.id ?? tag.label} className="inline-flex max-w-full items-center gap-1 rounded-full border border-[var(--ots-outline-variant)] px-2 py-0.5 text-[11px]">
        <span aria-hidden="true" className="size-2 shrink-0 rounded-full" style={{ backgroundColor: tag.colorHex ?? "var(--ots-outline)" }}/>
        <span className="truncate">{tag.label}</span>
      </li>
    ))}
  </ul>
);

const Failed = ({ title, source }: { title: string; source: ContainerDetailsSource<unknown> }) => (
  <OtsInlineAlert tone="danger" title={title}
    action={source.onRetry && <OtsActionButton size="compact" onClick={source.onRetry}>Try again</OtsActionButton>}>
    {source.error}
  </OtsInlineAlert>
);

/**
 * A tank at a glance, beside the container grid: today's hauls against its fill, the latest dispatch, its tickets as
 * Driver Activity rows, and its SCADA level with each haul drop paired to the ticket written for it.
 */
export const ContainerDetailsPanel = ({
  containerId,
  containerName,
  siteName,
  status,
  stalePoll,
  range,
  onRangeChange,
  onClose,
  tickets,
  levels,
  dispatch,
  dayStart,
  timeZone,
  auditScoreType,
  ticketParty,
  onOpenTicket,
  onOpenAudit,
  onOpenAttachments,
  className,
}: ContainerDetailsPanelProps) => {
  const [highlightKey, setHighlightKey] = useState<string>();
  const body = useRef<HTMLDivElement>(null);
  // A drop's ticket, picked from the Levels list, is brought into view in Tickets.
  useEffect(() => {
    if (highlightKey) body.current?.querySelector(`[data-ticket="${CSS.escape(highlightKey)}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [highlightKey]);
  const pickups = useMemo(() => pickupsFrom(tickets.data, containerId), [tickets.data, containerId]);
  const drops = useMemo(() => matchHaulDrops(detectHaulDrops(levels.data), pickups), [levels.data, pickups]);
  const summary = useMemo(
    () => summarizeContainerDetails({ pickups, drops, levels: levels.data, dispatch, dayStart }),
    [pickups, drops, levels.data, dispatch, dayStart],
  );
  // Newest first, like the tickets.
  const dropRows = useMemo(() => [...drops].reverse(), [drops]);
  const unticketed = drops.filter((drop) => !drop.tickets.length).length;
  // Bare lines only: labels cover the line at this width, and the Haul drops list below names each one. Unticketed drops are red.
  const markers = useMemo<OtsTimeSeriesMarker[]>(() => [
    ...pickups.filter((entry) => entry.time != null).map((entry) => ({ time: entry.time!, label: "" })),
    ...drops.filter((drop) => !drop.tickets.length).map((drop) => ({ time: drop.end, label: "", tone: "danger" as const })),
  ], [pickups, drops]);

  const lastHaul = summary.lastHaul;
  const open = summary.openDispatch;

  return (
    <div role="complementary" aria-label={`${containerName} details`} className={[
      "!flex min-h-0 flex-col overflow-hidden bg-[var(--ots-surface)] text-[var(--ots-text)]",
      className,
    ].filter(Boolean).join(" ")}>
      <header className="!flex shrink-0 items-start justify-between gap-2 px-3 pb-2 pt-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">{containerName}</h2>
          {siteName && <p className="truncate text-xs text-[var(--ots-text-muted)]">{siteName}</p>}
          {status && (
            <div className="mt-2 space-y-1.5">
              <ContainerStatusIndicators status={status} stalePoll={stalePoll}/>
              <Tags status={status}/>
            </div>
          )}
        </div>
        <OtsIconActionButton size="compact" variant="text" icon={<XMarkIcon/>} label="Close container details" onClick={onClose}/>
      </header>

      <div ref={body} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {status && (
          <Section title="Tank">
            <TankFacts status={status} timeZone={timeZone}/>
          </Section>
        )}
        <Section title="Hauls">
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Hauled today" value={`${summary.loadsToday} ${summary.loadsToday === 1 ? "load" : "loads"}`}
              detail={summary.loadsToday ? formatActivityQuantity(summary.hauledToday, summary.hauledUom) : undefined}/>
            <Tile label="Fill since last haul"
              value={summary.fillSinceLastHaul != null ? formatActivityQuantity(summary.fillSinceLastHaul, summary.fillUom) : "—"}
              detail={summary.fillSinceLastHaul == null && !levels.isLoading ? "No haul seen in the readings" : undefined}/>
            <Tile label="Last haul"
              value={lastHaul ? formatActivityDateTime(lastHaul.time, timeZone) : "—"}
              detail={lastHaul && [lastHaul.pickup?.personName, formatActivityQuantity(lastHaul.pickup?.quantityDecimal, lastHaul.pickup?.quantityUnitOfMeasure)].filter(Boolean).join(" · ")}/>
            <Tile label="Dispatch" tone={open ? "primary" : undefined}
              value={open ? `${open.status} · ${formatActivityTime(open.createDttm, timeZone)}` : "None open"}
              detail={open && [open.personName ?? "Unassigned", open.deliverySiteName].filter(Boolean).join(" → ")}/>
          </div>
        </Section>
        {/* The range only scopes what follows: tickets and readings. Tank and Hauls are always now and today. */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--ots-outline-variant)] px-3 py-2">
          <span className="text-[11px] text-[var(--ots-text-muted)]">Showing the last</span>
          <OtsSegmentedControl<ContainerDetailsRange> size="compact" className="[&_button]:!px-2 [&_button]:!py-0.5 [&_button]:!text-[11px]"
            ariaLabel="Time range" value={range} onChange={onRangeChange} segments={ranges}/>
        </div>

        <Section title="Tickets" aside={!tickets.isLoading && <span className="text-[11px] text-[var(--ots-text-muted)]">{tickets.data.length}</span>}>
          {tickets.error ? <Failed title="The tickets could not be loaded" source={tickets}/>
            : tickets.isLoading ? <OtsTableLoader variant="inline" label="Loading tickets"/>
            : <ContainerTicketList entries={tickets.data} containerId={containerId} timeZone={timeZone} auditScoreType={auditScoreType}
                highlightKey={highlightKey} party={ticketParty} onOpenTicket={onOpenTicket} onOpenAudit={onOpenAudit} onOpenAttachments={onOpenAttachments}/>}
        </Section>

        <Section title="Levels" aside={unticketed > 0 && <OtsBadge tone="danger" size="compact">{unticketed} without a ticket</OtsBadge>}>
          {levels.error ? <Failed title="The tank's readings could not be loaded" source={levels}/>
            : levels.isLoading ? <OtsTableLoader variant="inline" label="Loading the tank's readings"/>
            : <>
              <ContainerDetailsChart levels={levels.data} capacity={status?.containerVolume} markers={markers}/>
              {dropRows.length > 0 && (
                <>
                  <h4 className={[heading, "mb-1 mt-3"].join(" ")}>Haul drops</h4>
                  <ul aria-label="Haul drops" className="divide-y divide-[var(--ots-outline-variant)] text-xs">
                    {dropRows.map((drop) => (
                      <li key={drop.start} className="flex items-center justify-between gap-2 py-1.5">
                        <span className="tabular-nums text-[var(--ots-text-muted)]">{formatActivityDateTime(drop.end, timeZone)}</span>
                        <span className="font-semibold tabular-nums">−{formatActivityQuantity(drop.volume, drop.uom)}</span>
                        <span className="flex min-w-0 flex-1 justify-end gap-1">
                          {drop.tickets.length ? drop.tickets.map((entry) => (
                            <button key={entry.key} type="button"
                              className="truncate rounded font-mono font-semibold text-[var(--ots-primary)] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]"
                              onClick={() => setHighlightKey(entry.key)}>
                              {containerTicketId(entry)}
                            </button>
                          )) : <OtsBadge tone="danger" size="compact">No ticket</OtsBadge>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>}
        </Section>
      </div>
    </div>
  );
};
